import { MenuItem } from "@szhsin/react-menu";
import { moveToBottomAndSave, moveToTopAndSave, setIsDullAndSave, setIsFrozenAndSave, shuffleArrayAndSave } from "./functions";
import { useContext, useEffect, useState } from "react";
import { CardMenuContext, MenuItemKey, MenuType, PlayState, PlayableVariant, TAB_NAME_INDEX_MAP, optionsByMenuType, tabIndexes } from "./types";

export type CardMenuProps = {
    card: PlayableVariant,
    sourceId: keyof PlayState,
    menuType: MenuType,
    onItemClick: () => void
}

export function PlayDeckMenuItems(props: CardMenuProps) {
    const {
        setState,
        state,
        setHasMulliganed,
        setSelectedTab
    } = useContext(CardMenuContext);

    const [menuItemOptions, setMenuTypeOptions] = useState<Record<MenuItemKey, boolean>>({
        activate: false,
        breakZone: false,
        damageZone: false,
        deck: false,
        dull: false,
        field1: false,
        field2: false,
        field3: false,
        freeze: false,
        hand: false,
        limitBreak: false,
        removedFromGame: false,
        revealed: false,
        bottomOfDeck: false,
        topOfDeck: false,
        addCounter: false,
        removeCounter: false
    });

    useEffect(() => {
        setMenuTypeOptions(optionsByMenuType[props.menuType]);
    }, [props.menuType]);

    return (
        <>
            {
                menuItemOptions["hand"] &&
                <MenuItem
                    onClick={(event) => {
                        event.stopPropagation = true;
                        moveToTopAndSave(props.card, props.sourceId, "hand", state, setState, setHasMulliganed);
                        props.onItemClick();
                    }}
                >
                    Hand
                </MenuItem>
            }

            {
                (menuItemOptions["field1"] || menuItemOptions["field2"] || menuItemOptions["field3"]) && props.card.value.card.type !== "Summon" &&
                <MenuItem
                    onClick={() => {
                        switch (props.card.value.card.type) {
                            case "Forward":
                                moveToTopAndSave(props.card, props.sourceId, "field1", state, setState, setHasMulliganed);
                                break;
                            case "Backup":
                                moveToTopAndSave(props.card, props.sourceId, "field3", state, setState, setHasMulliganed);
                                break;
                            case "Monster":
                                moveToTopAndSave(props.card, props.sourceId, "field2", state, setState, setHasMulliganed);
                                break;
                            default:
                                moveToTopAndSave(props.card, props.sourceId, "field1", state, setState, setHasMulliganed);
                                break;
                        }

                        props.onItemClick();
                    }}
                >
                    Field
                </MenuItem>
            }

            {
                menuItemOptions["breakZone"] &&
                <MenuItem
                    onClick={() => {
                        moveToTopAndSave(props.card, props.sourceId, "breakZone", state, setState, setHasMulliganed);
                        props.onItemClick();
                    }}
                >
                    Break Zone
                </MenuItem>
            }

            {
                menuItemOptions["damageZone"] &&
                <MenuItem
                    onClick={() => {
                        moveToTopAndSave(props.card, props.sourceId, "damageZone", state, setState, setHasMulliganed);
                        props.onItemClick();
                    }}
                >
                    Damage Zone
                </MenuItem>
            }

            {
                menuItemOptions["removedFromGame"] &&
                <MenuItem
                    onClick={() => {
                        moveToTopAndSave(props.card, props.sourceId, "removedFromGame", state, setState, setHasMulliganed);
                        props.onItemClick();
                    }}
                >
                    Remove from Game
                </MenuItem>
            }

            {
                menuItemOptions["dull"] && props.card.value.card.type !== "Summon" && !props.card.value.isDull &&
                <MenuItem
                    onClick={() => {
                        setIsDullAndSave(props.card, true, state, setState);
                        props.onItemClick();
                    }}
                >
                    Dull
                </MenuItem>
            }

            {
                menuItemOptions["activate"] && props.card.value.card.type !== "Summon" && props.card.value.isDull &&
                <MenuItem
                    onClick={() => {
                        setIsDullAndSave(props.card, false, state, setState);
                        props.onItemClick();
                    }}
                >
                    Activate
                </MenuItem>
            }

            {
                menuItemOptions["freeze"] && props.card.value.card.type !== "Summon" && !props.card.value.isFrozen &&
                <MenuItem
                    onClick={() => {
                        setIsFrozenAndSave(props.card, true, state, setState);
                        props.onItemClick();
                    }}
                >
                    Freeze
                </MenuItem>
            }

            {
                menuItemOptions["freeze"] && props.card.value.card.type !== "Summon" && props.card.value.isFrozen &&
                <MenuItem
                    onClick={() => {
                        setIsFrozenAndSave(props.card, false, state, setState);
                        props.onItemClick();
                    }}
                >
                    Unfreeze
                </MenuItem>
            }

            {
                menuItemOptions["revealed"] &&
                <MenuItem
                    onClick={() => {
                        moveToTopAndSave(props.card, props.sourceId, "revealed", state, setState, setHasMulliganed);
                        setSelectedTab(TAB_NAME_INDEX_MAP["revealed-tab"]);
                        props.onItemClick();
                    }}
                >
                    Reveal / Look
                </MenuItem>
            }

            {
                menuItemOptions["topOfDeck"] &&
                <MenuItem
                    onClick={() => {
                        // Deck is reversed so needs to be sent to bottom.
                        moveToBottomAndSave(props.card, props.sourceId, "deck", state, setState, setHasMulliganed);
                        props.onItemClick();
                    }}
                >
                    Top of Deck
                </MenuItem>
            }

            {
                menuItemOptions["bottomOfDeck"] &&
                <MenuItem
                    onClick={() => {
                        // Deck is reversed so needs to be sent to top.
                        moveToTopAndSave(props.card, props.sourceId, "deck", state, setState, setHasMulliganed);
                        props.onItemClick();
                    }}
                >
                    Bottom of Deck
                </MenuItem>
            }

            {
                menuItemOptions["addCounter"] &&
                <MenuItem
                    onClick={() => {
                        setHasMulliganed(true);
                        const stateCopy = JSON.parse(JSON.stringify(state)) as PlayState;
                        const source = stateCopy[props.sourceId as keyof PlayState];
                        if (!source) {
                            return;
                        }

                        const match = source.find(x => x.id === props.card.id);
                        if (!match) {
                            return;
                        }

                        match.value.counter += 1;
                        setState(stateCopy);
                        props.onItemClick();
                    }}
                >
                    Counter +
                </MenuItem>
            }

            {
                menuItemOptions["removeCounter"] &&
                <MenuItem
                    onClick={() => {
                        setHasMulliganed(true);
                        const stateCopy = JSON.parse(JSON.stringify(state)) as PlayState;
                        const source = stateCopy[props.sourceId as keyof PlayState];
                        if (!source) {
                            return;
                        }

                        const match = source.find(x => x.id === props.card.id);
                        if (!match) {
                            return;
                        }

                        if (match.value.counter < 1) {
                            return;
                        }

                        match.value.counter -= 1;
                        setState(stateCopy);
                        props.onItemClick();
                    }}
                >
                    Counter -
                </MenuItem>
            }
        </>
    )
}
